import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { CircleAlert, Plus, Trash2, User } from 'lucide-react';
import { partnersApi, usersApi } from '@/lib/api';
import {
  getEmailErrorMessage,
  getPhoneErrorMessage,
  normalizePhoneNumber,
  filterPhoneInput,
  formatApiError,
} from '@/lib/validation';
import { Avatar, Button, Callout, Card, Combobox, EmptyState, IconButton, Skeleton } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { TextareaField, TextField } from '@/components/form/Felder';
import type { PartnerCreate } from '@/types';

const partnerTypeOptions = [
  { value: 'cleaning_company', label: 'Reinigungsfirma' },
  { value: 'netzwerkpartner', label: 'Netzwerkpartner' },
  { value: 'subcontractor', label: 'Subunternehmer' },
  { value: 'real_estate_agent', label: 'Immobilienmakler' },
  { value: 'architect', label: 'Architekt' },
  { value: 'other', label: 'Sonstiger' },
];

interface InlineContact {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  mobile: string;
  role: string;
  is_primary: boolean;
}

interface ContactErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  general?: string;
}

interface FormData extends PartnerCreate {}

export function PartnerFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  // State for inline contacts (only for new partners)
  const [contacts, setContacts] = useState<InlineContact[]>([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [newContact, setNewContact] = useState<InlineContact>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    role: '',
    is_primary: false,
  });
  const [contactErrors, setContactErrors] = useState<ContactErrors>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { data: partner, isLoading: isLoadingPartner } = useQuery({
    queryKey: ['partners', id],
    queryFn: () => partnersApi.get(id!),
    enabled: isEdit,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      partner_type: 'other',
      assigned_to: undefined,
      notes: '',
    },
    values: partner
      ? {
          name: partner.name,
          partner_type: partner.partner_type || 'other',
          assigned_to: partner.assigned_to || undefined,
          notes: partner.notes || '',
        }
      : undefined,
  });

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(isDirty || contacts.length > 0 || showContactForm);
  }, [isDirty, contacts.length, showContactForm]);

  // Warn before leaving with unsaved changes (browser back/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const createMutation = useMutation({
    mutationFn: partnersApi.create,
    onSuccess: (data) => {
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      navigate(`/partners/${data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PartnerCreate>) => partnersApi.update(id!, data),
    onSuccess: () => {
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      navigate(`/partners/${id}`);
    },
  });

  const onSubmit = (data: FormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      // Normalize phone numbers in contacts before creating
      const normalizedContacts = contacts.map((c) => ({
        ...c,
        phone: normalizePhoneNumber(c.phone),
        mobile: normalizePhoneNumber(c.mobile),
      }));
      createMutation.mutate({
        ...data,
        contacts: normalizedContacts.length > 0 ? normalizedContacts : undefined,
      });
    }
  };

  const validateContact = (contact: InlineContact): ContactErrors => {
    const errors: ContactErrors = {};

    if (!contact.first_name.trim()) {
      errors.first_name = 'Vorname fehlt';
    }
    if (!contact.last_name.trim()) {
      errors.last_name = 'Nachname fehlt';
    }

    const emailErr = getEmailErrorMessage(contact.email);
    if (emailErr) {
      errors.email = emailErr;
    }

    const phoneErr = getPhoneErrorMessage(contact.phone);
    if (phoneErr) {
      errors.phone = phoneErr;
    }

    const mobileErr = getPhoneErrorMessage(contact.mobile);
    if (mobileErr) {
      errors.mobile = mobileErr;
    }

    return errors;
  };

  const addContact = () => {
    const errors = validateContact(newContact);
    setContactErrors(errors);

    if (Object.keys(errors).length === 0) {
      const contactToAdd = {
        ...newContact,
        phone: normalizePhoneNumber(newContact.phone),
        mobile: normalizePhoneNumber(newContact.mobile),
        is_primary: contacts.length === 0,
      };
      setContacts([...contacts, contactToAdd]);
      setNewContact({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        mobile: '',
        role: '',
        is_primary: false,
      });
      setContactErrors({});
      setShowContactForm(false);
    }
  };

  const removeContact = (index: number) => {
    const updated = contacts.filter((_, i) => i !== index);
    if (contacts[index].is_primary && updated.length > 0) {
      updated[0].is_primary = true;
    }
    setContacts(updated);
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error || updateMutation.error;

  if (isEdit && isLoadingPartner) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-lg" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div>
      <CrmPageHeader
        size="page"
        zurueck={{ to: isEdit ? `/partners/${id}` : '/partners', label: 'Zurück' }}
        title={isEdit ? 'Partner bearbeiten' : 'Partner anlegen'}
      />

      {/* API Error */}
      {mutationError && (
        <Callout tone="danger" icon={CircleAlert} title="Speichern fehlgeschlagen" boxed className="mb-6">
          <span className="whitespace-pre-line">{formatApiError(mutationError)}</span>
        </Callout>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-6">
        {/* Partner Information */}
        <Card title="Partnerdaten" description="Grundlegende Informationen">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Firmenname fehlt' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Firmenname"
                    required
                    error={errors.name?.message}
                    placeholder="Reinigung Müller GmbH"
                  />
                )}
              />
              <Controller
                name="partner_type"
                control={control}
                render={({ field }) => (
                  <Combobox
                    value={field.value || ''}
                    onChange={field.onChange}
                    label="Partnertyp"
                    options={partnerTypeOptions}
                    placeholder="Typ wählen"
                  />
                )}
              />
              <Controller
                name="assigned_to"
                control={control}
                render={({ field }) => (
                  <Combobox
                    value={field.value || ''}
                    onChange={field.onChange}
                    label="Zuständiger Mitarbeiter"
                    options={users.map(u => ({ value: u.id, label: u.name }))}
                    placeholder="Mitarbeiter wählen"
                  />
                )}
              />
            </div>
        </Card>

        {/* Contacts - only show for new partners */}
        {!isEdit && (
          <Card
            title="Ansprechpartner"
            description="Kontaktpersonen beim Partner"
            actions={
              !showContactForm && (
                <Button type="button" size="sm" icon={Plus} onClick={() => setShowContactForm(true)}>
                  Hinzufügen
                </Button>
              )
            }
          >
              {/* Existing contacts */}
              {contacts.length > 0 && (
                <div className="space-y-3 mb-4">
                  {contacts.map((contact, index) => (
                    <div key={index} className="flex items-center justify-between rounded-lg bg-muted p-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={`${contact.first_name} ${contact.last_name}`} size="lg" />
                        <div>
                          <div className="font-medium">
                            {contact.first_name} {contact.last_name}
                            {contact.is_primary && <span className="ml-2 t-label text-highlight">Primär</span>}
                          </div>
                          <div className="t-small text-muted-foreground">
                            {[contact.role, contact.email, contact.phone, contact.mobile]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </div>
                      </div>
                      <IconButton icon={Trash2} label="Kontakt entfernen" variant="ghost" size="sm" onClick={() => removeContact(index)} />
                    </div>
                  ))}
                </div>
              )}

              {/* Add contact form */}
              {showContactForm && (
                <div className="rounded-lg border border-border p-4">
                  <p className="mb-4 t-small text-muted-foreground">Nur Vor- und Nachname sind Pflichtfelder.</p>
                  {contactErrors.general && (
                    <Callout tone="danger" icon={CircleAlert} title={contactErrors.general} boxed className="mb-4" />
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <TextField
                      label="Vorname"
                      required
                      value={newContact.first_name}
                      onChange={(e) => {
                        setNewContact({ ...newContact, first_name: e.target.value });
                        if (contactErrors.first_name) {
                          setContactErrors({ ...contactErrors, first_name: undefined });
                        }
                      }}
                      placeholder="Max"
                      error={contactErrors.first_name}
                    />
                    <TextField
                      label="Nachname"
                      required
                      value={newContact.last_name}
                      onChange={(e) => {
                        setNewContact({ ...newContact, last_name: e.target.value });
                        if (contactErrors.last_name) {
                          setContactErrors({ ...contactErrors, last_name: undefined });
                        }
                      }}
                      placeholder="Mustermann"
                      error={contactErrors.last_name}
                    />
                    <TextField
                      label="E-Mail"
                      type="email"
                      value={newContact.email}
                      onChange={(e) => {
                        setNewContact({ ...newContact, email: e.target.value });
                        if (contactErrors.email || contactErrors.general) {
                          setContactErrors({ ...contactErrors, email: undefined, general: undefined });
                        }
                      }}
                      placeholder="max@firma.de"
                      error={contactErrors.email || getEmailErrorMessage(newContact.email)}
                    />
                    <TextField
                      label="Position"
                      value={newContact.role}
                      onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                      placeholder="Geschäftsführer"
                    />
                    <TextField
                      label="Telefon (Festnetz)"
                      value={newContact.phone}
                      onChange={(e) => {
                        setNewContact({ ...newContact, phone: filterPhoneInput(e.target.value) });
                        if (contactErrors.phone || contactErrors.general) {
                          setContactErrors({ ...contactErrors, phone: undefined, general: undefined });
                        }
                      }}
                      onBlur={() => {
                        if (newContact.phone.trim()) {
                          setNewContact({ ...newContact, phone: normalizePhoneNumber(newContact.phone) });
                        }
                      }}
                      placeholder="0351 1234567"
                      error={contactErrors.phone || getPhoneErrorMessage(newContact.phone)}
                      hint={!contactErrors.phone && !getPhoneErrorMessage(newContact.phone) ? "Wird beim Verlassen des Felds formatiert" : undefined}
                    />
                    <TextField
                      label="Mobil"
                      value={newContact.mobile}
                      onChange={(e) => {
                        setNewContact({ ...newContact, mobile: filterPhoneInput(e.target.value) });
                        if (contactErrors.mobile || contactErrors.general) {
                          setContactErrors({ ...contactErrors, mobile: undefined, general: undefined });
                        }
                      }}
                      onBlur={() => {
                        if (newContact.mobile.trim()) {
                          setNewContact({ ...newContact, mobile: normalizePhoneNumber(newContact.mobile) });
                        }
                      }}
                      placeholder="0170 1234567"
                      error={contactErrors.mobile || getPhoneErrorMessage(newContact.mobile)}
                      hint={!contactErrors.mobile && !getPhoneErrorMessage(newContact.mobile) ? "Wird beim Verlassen des Felds formatiert" : undefined}
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setShowContactForm(false);
                        setContactErrors({});
                        setNewContact({
                          first_name: '',
                          last_name: '',
                          email: '',
                          phone: '',
                          mobile: '',
                          role: '',
                          is_primary: false,
                        });
                      }}
                    >
                      Abbrechen
                    </Button>
                    <Button type="button" size="sm" variant="primary" onClick={addContact}>
                      Kontakt hinzufügen
                    </Button>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {contacts.length === 0 && !showContactForm && (
                <EmptyState
                  icon={User}
                  title="Noch keine Ansprechpartner"
                  description="Ansprechpartner erscheinen hier, sobald einer hinzugefügt ist."
                  className="py-6"
                />
              )}
          </Card>
        )}

        {/* Notes */}
        <Card title="Notizen" description="Interne Anmerkungen zum Partner">
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextareaField {...field} rows={4} placeholder="Interne Notizen" />
              )}
            />
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(isEdit ? `/partners/${id}` : '/partners')}
            disabled={isSubmitting}
          >
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isEdit ? 'Änderungen speichern' : 'Partner anlegen'}
          </Button>
        </div>
      </form>
    </div>
  );
}
